import React, { useState } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip
} from '@mui/material';
import { ExpandMore, Login, Apartment, People, Receipt, WaterDrop, AttachMoney, Dashboard, Assessment, MeetingRoom, AccountBalance, Person, Help as HelpIcon } from '@mui/icons-material';

const helpSections = [
  // {
  //   title: 'Getting Started',
  //   icon: <Login />,
  //   content: [
  //     {
  //       subtitle: 'Sign Up (Create New Apartment)',
  //       steps: [
  //         'Open the application and click the "Sign Up" tab',
  //         'Fill in your details: Full Name, Email, Phone Number, Password',
  //         'Fill in Apartment details: Name, Address, Total Flats',
  //         'Enter Opening Fund Balance — the total fund your apartment currently has from previous records (optional)',
  //         'Enter your Flat Number',
  //         'Click "Sign Up" — you will be logged in as Admin'
  //       ]
  //     },
  //     {
  //       subtitle: 'Sign In',
  //       steps: [
  //         'Enter your Email or 10-digit Phone Number',
  //         'Enter your Password',
  //         'Click "Sign In"'
  //       ]
  //     },
  //     {
  //       subtitle: 'Forgot Password',
  //       steps: [
  //         'Click "Forgot Password" on the login page',
  //         'Enter your registered email or phone number',
  //         'Check your email for the reset link',
  //         'Click the link and set a new password'
  //       ]
  //     }
  //   ]
  // },
  {
    title: 'My Apartment',
    icon: <Apartment />,
    content: [
      {
        subtitle: 'Edit Apartment Details',
        steps: [
          'Go to My Apartment from the sidebar',
          'Click the Edit (pencil) icon',
          'Update: Name, Address, Total Flats, Opening Fund Balance',
          'Optionally add: Logo, Payment QR Code, Association Name, Bank Details, UPI ID',
          'Click "Update"'
        ]
      },
      {
        subtitle: 'Opening Fund Balance',
        steps: [
          'This is the total money your apartment had before using this app',
          'Example: If your apartment had ₹50,000 in the bank, enter ₹50,000',
          'This is included in the Overall Balance on the Dashboard',
          'You can update it anytime from My Apartment → Edit'
        ]
      }
    ]
  },
  {
    title: 'Members',
    icon: <People />,
    content: [
      {
        subtitle: 'Add a New Member',
        steps: [
          'Go to Members from the sidebar',
          'Click "+ Add Member"',
          'Fill in: Full Name, Email, Phone, Password, Flat Number',
          'Choose if they are an Owner and/or Admin',
          'Click "Create & Add Member"',
          'Share the password with the member so they can login'
        ]
      },
      {
        subtitle: 'Edit a Member',
        steps: [
          'Click the Edit (pencil) icon next to the member',
          'Toggle Admin access, Owner status, or update Opening Balance',
          'Click "Save Changes"'
        ]
      },
      {
        subtitle: 'Remove a Member',
        steps: [
          'Click the Delete (trash) icon',
          'Confirm the removal — member will be permanently removed'
        ]
      }
    ]
  },
  {
    title: 'Expenses',
    icon: <Receipt />,
    content: [
      {
        subtitle: 'Add an Expense',
        steps: [
          'Go to Expenses from the sidebar',
          'Click "+ Add Expense"',
          'Select Category (e.g., Watchman Salary, Electricity Bill)',
          'Enter Description (e.g., "May watchman salary")',
          'Choose Split Type: Equal Share or Per Flat',
          'Enter Amount and select Date',
          'Optionally upload a Bill Image (max 2MB)',
          'Click "Add"'
        ]
      },
      {
        subtitle: 'Approve an Expense',
        steps: [
          'Find the expense with "Pending" status',
          'Click the ✓ (green check) icon to approve',
          'Only approved expenses are included in dashboard totals'
        ]
      },
      {
        subtitle: 'Reject an Expense',
        steps: [
          'Click the ✗ (red cross) icon',
          'Enter an optional rejection reason',
          'The expense will show as "Rejected" (greyed out)'
        ]
      },
      {
        subtitle: 'Edit an Expense',
        steps: [
          'Click the Edit (pencil) icon',
          'Modify category, description, amount, date',
          'You can upload/replace the bill image',
          'Click "Update"'
        ]
      }
    ]
  },
  {
    title: 'Water Bill Calculator',
    icon: <WaterDrop />,
    content: [
      {
        subtitle: 'How It Works',
        steps: [
          'Go to Water Bill from the sidebar',
          'Enter the Total Water Bill Amount (from your water supplier)',
          'For each flat, enter Previous Reading and Current Reading',
          'Units Consumed auto-calculates (Current - Previous)',
          'Click "Calculate" — system calculates Cost Per Unit',
          'Each flat\'s amount = Their Units × Cost Per Unit',
          'Click "Save Water Bill as Expense" to save'
        ]
      },
      {
        subtitle: 'Example',
        steps: [
          'Total Bill: ₹5,000',
          'Flat 101: 50 units, Flat 102: 100 units, Flat 103: 100 units',
          'Total Units: 250',
          'Cost Per Unit: ₹5,000 ÷ 250 = ₹20/unit',
          'Flat 101 pays: 50 × ₹20 = ₹1,000',
          'Flat 102 pays: 100 × ₹20 = ₹2,000',
          'Flat 103 pays: 100 × ₹20 = ₹2,000'
        ]
      }
    ]
  },
  {
    title: 'Contributions',
    icon: <AttachMoney />,
    content: [
      {
        subtitle: 'Add a Contribution',
        steps: [
          'Go to Contributions from the sidebar',
          'Click "+ Add Contribution"',
          'Select the Flat/Resident who paid',
          'Enter Amount, Date, and Month/Year',
          'Add optional Notes (e.g., "May maintenance")',
          'Click "Add"'
        ]
      }
    ]
  },
  {
    title: 'Dashboard',
    icon: <Dashboard />,
    content: [
      {
        subtitle: 'Monthly View (Top Row)',
        steps: [
          'Total Expenses — All approved expenses for the selected month',
          'Contributions — All contributions received this month',
          'Monthly Balance — Contributions minus Expenses for this month'
        ]
      },
      {
        subtitle: 'Overall Summary (Bottom Row)',
        steps: [
          'Opening Balance — Fund carried from old records',
          'Total Collected (All Time) — Opening + All contributions ever',
          'Total Spent (All Time) — All approved expenses ever',
          'Overall Balance — What\'s left in the fund (highlighted)'
        ]
      }
    ]
  },
  {
    title: 'Reports',
    icon: <Assessment />,
    content: [
      {
        subtitle: 'Download Excel Report',
        steps: [
          'Go to Reports from the sidebar',
          'Select Apartment, Month, and Year',
          'Click "Download Excel Report"',
          'Excel contains: Expenses Sheet, Contributions Sheet, Summary Sheet'
        ]
      }
    ]
  },
  {
    title: 'Function Hall Booking',
    icon: <MeetingRoom />,
    content: [
      {
        subtitle: 'Book the Function Hall',
        steps: [
          'Go to Function Hall from the sidebar',
          'Click "Book Hall"',
          'Select the Resident who is booking',
          'Choose a Date (only future dates allowed)',
          'Check slot availability (green = free, red = booked)',
          'Select Slot: Full Day, Morning, or Evening',
          'Enter Amount and Payment Status',
          'Click "Book"'
        ]
      },
      {
        subtitle: 'Booking Rules',
        steps: [
          'Full Day booked → Morning and Evening unavailable',
          'Morning booked → Full Day unavailable, Evening still free',
          'Evening booked → Full Day unavailable, Morning still free',
          'Past dates cannot be booked'
        ]
      }
    ]
  },
  {
    title: 'Corpus Fund',
    icon: <AccountBalance />,
    content: [
      {
        subtitle: 'About Corpus Fund',
        steps: [
          'For large one-time contributions (e.g., generator, CCTV)',
          'Add Corpus Contribution — Record one-time payments from owners',
          'Add Corpus Expense — Record large purchases from the fund',
          'Summary shows total contributions, expenses, and remaining balance'
        ]
      }
    ]
  },
  {
    title: 'Profile & Password',
    icon: <Person />,
    content: [
      {
        subtitle: 'Update Profile',
        steps: [
          'Click your name in the top-right corner',
          'Update Full Name or Phone Number',
          'Click "Update Profile"'
        ]
      },
      {
        subtitle: 'Change Password',
        steps: [
          'On the Profile page, enter New Password',
          'Confirm it and click "Change Password"'
        ]
      }
    ]
  }
];

export default function Help() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Help & User Guide</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Learn how to use Apartment Expense Manager. Click on any section below to expand.
      </Typography>

      {/* Roles Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Roles & Permissions</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Feature</strong></TableCell>
                <TableCell align="center"><strong>SuperAdmin</strong></TableCell>
                <TableCell align="center"><strong>Admin</strong></TableCell>
                <TableCell align="center"><strong>Normal</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ['Create/Delete Apartment', true, false, false],
                ['Edit Apartment', true, true, false],
                ['Add/Edit/Remove Members', true, true, false],
                ['Add Expense', true, true, false],
                ['Approve/Reject Expense', true, true, false],
                ['Add Contribution', true, true, false],
                ['View Dashboard & Data', true, true, true],
                ['Download Reports', true, true, true],
                ['Water Bill Calculator', true, true, false],
                ['Function Hall Booking', true, true, true],
              ].map(([feature, sa, admin, normal], idx) => (
                <TableRow key={idx}>
                  <TableCell>{feature}</TableCell>
                  <TableCell align="center">{sa ? '✅' : '❌'}</TableCell>
                  <TableCell align="center">{admin ? '✅' : '❌'}</TableCell>
                  <TableCell align="center">{normal ? '✅' : '❌'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Help Sections */}
      {helpSections.map((section, idx) => (
        <Accordion key={idx} expanded={expanded === idx} onChange={handleChange(idx)} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {section.icon}
              <Typography variant="subtitle1" fontWeight={600}>{section.title}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {section.content.map((item, subIdx) => (
              <Box key={subIdx} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>{item.subtitle}</Typography>
                <Box component="ol" sx={{ pl: 2, m: 0 }}>
                  {item.steps.map((step, stepIdx) => (
                    <li key={stepIdx} style={{ marginBottom: '4px', fontSize: '14px' }}>{step}</li>
                  ))}
                </Box>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Support Footer */}
      <Paper sx={{ p: 2, mt: 3, textAlign: 'center', bgcolor: '#e3f2fd' }}>
        <Typography variant="body2">
          Need more help? Contact us at <a href="mailto:info@flatexpense.com" style={{ color: '#1976d2', fontWeight: 'bold' }}>info@flatexpense.com</a>
        </Typography>
      </Paper>
    </Box>
  );
}
